import React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Collapse,
  Alert,
  Chip,
  Divider,
  Card,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import Client from '../Client';
import Config from '../ClientConfig';
import { locationsList } from './simpleBiReportsApi';

class RapDecisions extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: props.client || new Client('none'),

      locations: [],
      products: [],
      productNameMap: {},

      decisionsFilters: {
        period: dayjs().format('YYYY-MM'),
        location: '',
        product: ''
      },

      decisionsAll: [],
      decisionsRows: [],

      decisionsPage: 0,
      decisionsRpp: -1,
      filtersOpen: false,

      decisionsLoaded: false,
      decisionsLoading: false,
    };
  }

  componentDidMount() {
    this.fetchLookups();
  }

  formatQty = (val) => {
    if (val == null || val === '') return '';

    const num = Number(val);

    if (!Number.isFinite(num)) return String(val);

    const rounded = Math.round(num * 1000) / 1000;

    return rounded.toString();
  };

  buildDecisionsQuery = () => {
    const { period, location } = this.state.decisionsFilters;
    const [year, month] = (period || '').split('-');

    const q = { year, month };

    if (location) q.location = location;

    return q;
  };

  getLocationLabel = () => {
    const { decisionsFilters, locations } = this.state;

    const locObj = (locations || []).find(
      (l) => String(l.iref || l.uid || l.id) === String(decisionsFilters.location)
    );

    return locObj ? locObj.name || locObj.description : '';
  };

  fetchLookups() {
    const { client } = this.state;

    const locationsResp = locationsList(client) || {};

    const productsResp = client.getData(
      Config.API_URL_PRODUCT + 'list_products/',
      {
        status: 2,
        orderBy_name: true,
        askQuantity: 1,
        isPurchased: 1
      }
    ) || {};

    const locations = Array.isArray(locationsResp.locations)
      ? locationsResp.locations
      : [];

    locations.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    const products = Array.isArray(productsResp.products)
      ? productsResp.products
      : [];

    const productNameMap = {};

    products.forEach((p) => {
      const pid = p.iref || p.uid || p.code || p.product || p.id;
      const pname = p.name || p.productName || p.description || p.code || pid;

      if (pid) {
        productNameMap[String(pid)] = String(pname);
      }
    });

    this.setState({
      locations,
      products,
      productNameMap
    });
  }

  fetchDecisions = () => {
    const { client, decisionsFilters } = this.state;

    if (!decisionsFilters.location) {
      this.setState({
        decisionsAll: [],
        decisionsRows: [],
        decisionsLoaded: true,
        decisionsLoading: false,
        decisionsPage: 0,
      });

      return;
    }

    this.setState({
      decisionsLoading: true,
      decisionsPage: 0,
      decisionsRpp: -1
    });

    const query = this.buildDecisionsQuery();

    const resp = client.getData(
      `${Config.API_URL_REPORTS_TRANSACTIONS}month_decisions/`,
      query
    ) || {};

    const list = Array.isArray(resp.results) ? resp.results : [];

    const prod = decisionsFilters.product || '';
    const filtered = prod
      ? list.filter((r) => String(r.productIref) === String(prod))
      : list;

    this.setState({
      decisionsAll: list,
      decisionsRows: filtered,
      decisionsLoading: false,
      decisionsLoaded: true,
    });
  };

  resetFilters = () => {
    this.setState({
      decisionsFilters: {
        period: dayjs().format('YYYY-MM'),
        location: '',
        product: ''
      },
      decisionsAll: [],
      decisionsRows: [],
      decisionsLoaded: false,
      decisionsLoading: false,
      decisionsPage: 0,
    });
  };

  exportCSV = () => {
    const { decisionsRows, productNameMap, decisionsFilters } = this.state;

    if (!decisionsRows || !decisionsRows.length) return;

    const locationLabel = this.getLocationLabel() || decisionsFilters.location || '-';

    let productLabel = '-';

    if (decisionsFilters.product) {
      const pObj = (this.state.products || []).find(
        (p) => String(p.iref || p.id || p.uid || p.code) === String(decisionsFilters.product)
      );

      productLabel =
        pObj?.name ||
        pObj?.productName ||
        pObj?.description ||
        pObj?.code ||
        String(decisionsFilters.product);
    }

    const metaLines = [
      `"Inventory Decisions Export"`,
      `"Period: ${decisionsFilters.period || '-'}"`,
      `"Location: ${locationLabel}"`,
      `"Product Filter: ${productLabel}"`,
      `"Exported At: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}"`,
      '',
    ];

    const header = ['Product', 'System', 'Counted', 'Delta'];

    const lines = decisionsRows.map((r) => {
      const productLabelRow =
        r.productName ||
        productNameMap[String(r.productIref)] ||
        r.productIref;

      return [
        `"${productLabelRow}"`,
        this.formatQty(r.calcQty),
        this.formatQty(r.decisionQty),
        this.formatQty(r.delta),
      ].join(',');
    });

    const csv = '\uFEFF' + [...metaLines, header.join(','), ...lines].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute(
      'download',
      `decisions_${dayjs().format('YYYYMMDD_HHmmss')}.csv`
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  renderFilterBar = () => {
    const { decisionsFilters, filtersOpen, decisionsRows } = this.state;

    const locLabel = this.getLocationLabel();
    const hasRows = !!decisionsRows?.length;
    const locationMissing = !decisionsFilters.location;

    return (
      <Box
        sx={{
          px: {
            xs: 1,
            sm: 1.5
          },
          py: {
            xs: 0.75,
            sm: 1
          },
          borderBottom: '1px solid rgba(0,0,0,0.08)'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap'
          }}
        >
          <Chip
            size="small"
            label={decisionsFilters.period || '-'}
            sx={this.chipSx()}
          />

          <Chip
            size="small"
            label={locLabel ? locLabel : 'Select Location'}
            color={locationMissing ? 'warning' : 'default'}
            variant={locationMissing ? 'outlined' : 'filled'}
            sx={this.chipSx()}
          />

          {!!decisionsFilters.product && (
            <Chip
              size="small"
              label="Product filtered"
              variant="outlined"
              sx={this.chipSx()}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />

          <IconButton
            size="small"
            onClick={this.exportCSV}
            disabled={!hasRows}
            aria-label="export"
            sx={{
              display: {
                xs: 'none',
                sm: 'inline-flex'
              }
            }}
          >
            <FileDownloadIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            onClick={() => this.setState({ filtersOpen: !filtersOpen })}
            aria-label="toggle filters"
          >
            {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 0.75 }} />

          <Box
            sx={{
              display: 'grid',
              gap: 1,
              alignItems: 'center',
              gridTemplateColumns: {
                xs: '1fr 1fr',
                sm: '180px 240px 1fr'
              },
            }}
          >
            <TextField
              label="Period"
              type="month"
              value={decisionsFilters.period}
              onChange={(e) =>
                this.setState(
                  {
                    decisionsFilters: {
                      ...decisionsFilters,
                      period: e.target.value
                    }
                  },
                  () => {
                    if (this.state.decisionsFilters.location) {
                      this.fetchDecisions();
                    }
                  }
                )
              }
              size="small"
              margin="dense"
              InputLabelProps={{ shrink: true }}
              sx={this.filterSx()}
            />

            <TextField
              select
              required
              label="Location"
              value={decisionsFilters.location}
              onChange={(e) =>
                this.setState(
                  {
                    decisionsFilters: {
                      ...decisionsFilters,
                      location: e.target.value
                    },
                    decisionsPage: 0,
                  },
                  () => this.fetchDecisions()
                )
              }
              size="small"
              margin="dense"
              error={locationMissing}
              sx={this.filterSx()}
            >
              <MenuItem value="">Select location…</MenuItem>

              {(this.state.locations || []).map((loc) => (
                <MenuItem
                  key={String(loc.iref || loc.uid || loc.id)}
                  value={String(loc.iref || loc.uid || loc.id)}
                >
                  {loc.name ||
                    loc.description ||
                    String(loc.iref || loc.uid || loc.id)}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
              <Autocomplete
                size="small"
                clearOnEscape
                disableClearable={false}
                value={
                  (this.state.products || [])
                    .map((p) => ({
                      value: String(p.iref || p.id || p.uid || p.code),
                      label:
                        p.name ||
                        p.productName ||
                        p.description ||
                        p.code ||
                        String(p.iref || p.id || p.uid || p.code),
                    }))
                    .find((opt) => opt.value === String(decisionsFilters.product)) || null
                }
                options={(this.state.products || []).map((p) => ({
                  value: String(p.iref || p.id || p.uid || p.code),
                  label:
                    p.name ||
                    p.productName ||
                    p.description ||
                    p.code ||
                    String(p.iref || p.id || p.uid || p.code),
                }))}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                onChange={(_e, opt) => {
                  const newVal = opt ? opt.value : '';

                  this.setState((prev) => {
                    const all = prev.decisionsAll || [];
                    const rows = newVal
                      ? all.filter((r) => String(r.productIref) === String(newVal))
                      : all;

                    return {
                      decisionsFilters: {
                        ...prev.decisionsFilters,
                        product: newVal
                      },
                      decisionsRows: rows,
                      decisionsPage: 0,
                    };
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product"
                    margin="dense"
                    sx={this.filterSx()}
                  />
                )}
              />
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  };

  renderDecisionsTable = () => {
    const {
      decisionsRows,
      decisionsLoading,
      decisionsPage,
      decisionsRpp,
      productNameMap
    } = this.state;

    const paged =
      decisionsRpp > 0
        ? (decisionsRows || []).slice(
            decisionsPage * decisionsRpp,
            decisionsPage * decisionsRpp + decisionsRpp
          )
        : (decisionsRows || []);

    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            '& .MuiTableCell-root': {
              px: {
                xs: 0.75,
                sm: 1
              },
              py: {
                xs: 0.8,
                sm: 0.7
              },
              fontSize: {
                xs: '13.5px',
                sm: '14px',
                md: '15px'
              },
            }
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={this.headerCellSx('left')}>
                  <b>Product</b>
                </TableCell>

                <TableCell sx={this.headerCellSx('right', 78)} align="right">
                  <b>System</b>
                </TableCell>

                <TableCell sx={this.headerCellSx('right', 78)} align="right">
                  <b>Counted</b>
                </TableCell>

                <TableCell sx={this.headerCellSx('right', 58)} align="right">
                  <b>Δ</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paged.map((r) => {
                const productLabel =
                  r.productName ||
                  productNameMap[String(r.productIref)] ||
                  r.productIref;

                const deltaNum = Number(r.delta);
                const deltaColor =
                  deltaNum > 0 ? 'green' : deltaNum < 0 ? 'red' : undefined;

                return (
                  <TableRow
                    key={r.iref || `${r.productIref}-${r.date}`}
                    hover
                  >
                    <TableCell
                      title={r.productIref}
                      sx={this.bodyCellSx('left', false)}
                    >
                      {productLabel}
                    </TableCell>

                    <TableCell sx={this.bodyCellSx('right')} align="right">
                      {this.formatQty(r.calcQty)}
                    </TableCell>

                    <TableCell sx={this.bodyCellSx('right')} align="right">
                      {this.formatQty(r.decisionQty)}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        ...this.bodyCellSx('right', false),
                        color: deltaColor
                      }}
                    >
                      {this.formatQty(r.delta)}
                    </TableCell>
                  </TableRow>
                );
              })}

              {!paged.length && !decisionsLoading && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{
                      py: 2,
                      fontSize: {
                        xs: '13.5px',
                        sm: '14px'
                      }
                    }}
                  >
                    No data
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={(decisionsRows || []).length}
          page={decisionsPage}
          onPageChange={(_e, p) => this.setState({ decisionsPage: p })}
          rowsPerPage={decisionsRpp}
          onRowsPerPageChange={(e) =>
            this.setState({
              decisionsRpp: Number(e.target.value),
              decisionsPage: 0
            })
          }
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          sx={{
            borderTop: '1px solid rgba(0,0,0,0.08)',
            '.MuiTablePagination-toolbar': {
              minHeight: {
                xs: 46,
                sm: 52
              },
              px: {
                xs: 1,
                sm: 2
              }
            },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontSize: {
                xs: '13px',
                sm: '13px'
              }
            },
            '.MuiTablePagination-actions button': {
              padding: {
                xs: '4px',
                sm: '8px'
              }
            }
          }}
        />

        {decisionsLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1.5,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    );
  };

  render() {
    const { decisionsFilters } = this.state;
    const locationMissing = !decisionsFilters.location;

    return (
      <Card
        sx={{
          borderRadius: '14px',
          overflow: 'hidden',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {this.renderFilterBar()}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {locationMissing ? (
            <Box
              sx={{
                p: {
                  xs: 1,
                  sm: 1.5
                }
              }}
            >
              <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                Please select a Location to display data.
              </Alert>
            </Box>
          ) : (
            this.renderDecisionsTable()
          )}
        </Box>
      </Card>
    );
  }

  filterSx() {
    return {
      width: '100%',
      minWidth: 0,
      '& .MuiInputBase-input': {
        fontSize: {
          xs: '14px',
          sm: '14px'
        }
      },
      '& .MuiInputLabel-root': {
        fontSize: {
          xs: '13px',
          sm: '13px'
        }
      }
    };
  }

  chipSx() {
    return {
      height: {
        xs: 26,
        sm: 28
      },
      '& .MuiChip-label': {
        fontSize: {
          xs: '12px',
          sm: '13px'
        }
      }
    };
  }

  headerCellSx(align, minWidth = null) {
    return {
      textAlign: align,
      whiteSpace: 'nowrap',
      minWidth: minWidth,
      fontSize: {
        xs: '13px',
        sm: '13px',
        md: '14px'
      },
      backgroundColor: '#f6f4ef'
    };
  }

  bodyCellSx(align, strong = false) {
    return {
      textAlign: align,
      whiteSpace: align === 'left' ? 'normal' : 'nowrap',
      fontSize: {
        xs: '13.5px',
        sm: '13px',
        md: '14px'
      },
      fontWeight: strong ? 600 : 400,
      lineHeight: 1.35
    };
  }
}

export default RapDecisions;
